'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { detectNetworkSpeed, preloadMedia, type NetworkSpeed } from '@/lib/network-detector';
interface BackgroundCacheData {
  imageUrl: string | null;
  videoUrl: string | null;
  networkSpeed: NetworkSpeed | null;
  selectedMediaUrl: string | null;
  selectedMediaType: 'image' | 'video' | null;
  isReady: boolean;
}
interface BackgroundCacheContextType {
  cacheData: BackgroundCacheData;
  initializeCache: (imageUrl: string, videoUrl: string) => Promise<void>;
  isInitializing: boolean;
}
const BackgroundCacheContext = createContext<BackgroundCacheContextType | undefined>(undefined);

// SessionStorage keys
const STORAGE_KEY = 'alright_background_cache';
const STORAGE_TIMESTAMP_KEY = 'alright_background_cache_timestamp';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Load cached data from sessionStorage if valid
 */
function loadFromStorage(): BackgroundCacheData | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    const timestamp = sessionStorage.getItem(STORAGE_TIMESTAMP_KEY);
    if (!cached || !timestamp) return null;
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_DURATION) {
      // Cache expired
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      return null;
    }
    return JSON.parse(cached);
  } catch {
    return null;
  }
}

/**
 * Save cache data to sessionStorage
 */
function saveToStorage(data: BackgroundCacheData): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    sessionStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to save background cache:', error);
  }
}
export const useBackgroundCache = () => {
  const context = useContext(BackgroundCacheContext);
  if (!context) {
    throw new Error("useBackgroundCache must be used within a BackgroundCacheProvider");
  }
  return context;
};
export const BackgroundCacheProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [cacheData, setCacheData] = useState<BackgroundCacheData>(() => {
    // Try to load from storage on mount
    const stored = loadFromStorage();
    return stored || {
      imageUrl: null,
      videoUrl: null,
      networkSpeed: null,
      selectedMediaUrl: null,
      selectedMediaType: null,
      isReady: false
    };
  });
  const [isInitializing, setIsInitializing] = useState(false);

  /**
   * Initialize cache with image and video URLs
   * This performs network detection, selects appropriate media, and preloads it
   */
  const initializeCache = useCallback(async (imageUrl: string, videoUrl: string) => {
    // Don't re-initialize if already ready with same URLs
    if (cacheData.isReady && cacheData.imageUrl === imageUrl && cacheData.videoUrl === videoUrl) {
      return;
    }
    setIsInitializing(true);
    try {
      // Detect network speed (uses internal caching)
      const speed = await detectNetworkSpeed(videoUrl);

      // Select media based on network speed
      const shouldUseVideo = speed === 'fast' && videoUrl;
      const selectedUrl = shouldUseVideo ? videoUrl : imageUrl;
      const selectedType: 'image' | 'video' = shouldUseVideo ? 'video' : 'image';

      // Preload the selected media
      if (selectedUrl) {
        await preloadMedia(selectedUrl, selectedType);
      }

      // Update cache
      const newCacheData: BackgroundCacheData = {
        imageUrl,
        videoUrl,
        networkSpeed: speed,
        selectedMediaUrl: selectedUrl,
        selectedMediaType: selectedType,
        isReady: true
      };
      setCacheData(newCacheData);
      saveToStorage(newCacheData);
    } catch (error) {
      console.error('Failed to initialize background cache:', error);

      // Fallback to image on error
      const fallbackData: BackgroundCacheData = {
        imageUrl,
        videoUrl,
        networkSpeed: 'slow',
        selectedMediaUrl: imageUrl,
        selectedMediaType: 'image',
        isReady: true
      };
      setCacheData(fallbackData);
      saveToStorage(fallbackData);
    } finally {
      setIsInitializing(false);
    }
  }, [cacheData.isReady, cacheData.imageUrl, cacheData.videoUrl]);
  return <BackgroundCacheContext.Provider value={{
    cacheData,
    initializeCache,
    isInitializing
  }}>
      {children}
    </BackgroundCacheContext.Provider>;
};